import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import AdminLayout from "@/components/AdminLayout";
import { http } from "@/lib/api";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ExternalLink, Check } from "lucide-react";

const CATS = ["PRODUCT_IDENTITY", "MATERIALS", "ORIGIN", "REPAIR_CARE", "END_OF_LIFE", "FOOTPRINT", "CERTIFICATIONS"];
const LABELS = { PRODUCT_IDENTITY: "Product identity", MATERIALS: "Materials", ORIGIN: "Where it's made", REPAIR_CARE: "Repair & care", END_OF_LIFE: "End of life", FOOTPRINT: "Footprint", CERTIFICATIONS: "Certifications" };

export default function AdminApproval() {
  const [tasks, setTasks] = useState([]);
  const [active, setActive] = useState(null);
  const [edits, setEdits] = useState({ checklist: {}, summary_en: "" });
  const [selected, setSelected] = useState(new Set());
  const [batchBusy, setBatchBusy] = useState(false);
  const [batchDiffOpen, setBatchDiffOpen] = useState(false);
  const [history, setHistory] = useState([]);
  const [filterApprover, setFilterApprover] = useState("");
  const [filterSector, setFilterSector] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();

  const load = () => {
    http.get("/admin/pipeline/approvals").then((r) => setTasks(r.data.tasks || []));
  };
  const loadHistory = () => {
    const qs = new URLSearchParams({ limit: "20" });
    if (filterApprover.trim()) qs.set("approver", filterApprover.trim());
    if (filterSector.trim()) qs.set("sector", filterSector.trim());
    http.get(`/admin/pipeline/approvals/history?${qs.toString()}`)
      .then((r) => setHistory(r.data.entries || []))
      .catch(() => setHistory([]));
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { loadHistory(); }, [filterApprover, filterSector]);

  // Deep-link: opening /admin/approvals?task=<id> auto-opens that task.
  // Falls back gracefully when the id no longer exists (e.g. already
  // approved) — logs a friendly toast + strips the query param.
  useEffect(() => {
    const tid = searchParams.get("task");
    if (!tid || tasks.length === 0 || active) return;
    const found = tasks.find((t) => t.id === tid);
    if (found) {
      open(found);
    } else {
      toast.message(
        `Task ${tid.slice(0, 8)} not in the pending queue (probably already approved).`,
      );
      searchParams.delete("task");
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tasks]);

  const open = (t) => {
    setActive(t);
    setEdits({
      checklist: Object.fromEntries((t.draft?.checklist || []).map((c) => [c.category, { status: c.status, evidence_excerpt: (c.evidence_excerpt || {}).en || "" }])),
      summary_en: (t.draft?.summary || {}).en || "",
    });
  };

  const approve = async () => {
    const slug = active.brand?.slug;
    try {
      await http.post(`/admin/pipeline/approvals/${active.id}/approve`, {
        corrections: {
          checklist: CATS.map((cat) => ({ category: cat, ...edits.checklist[cat] })),
          summary_en: edits.summary_en,
        },
      });
      // Persistent confirmation toast with a public-profile link so the
      // editor can verify the publication in one click.
      toast.success(
        <span data-testid={`approve-confirmation-toast-${slug}`}>
          Approved &amp; published as baseline v1.{" "}
          <Link to={`/brands/${slug}`} target="_blank" className="underline font-mono">
            View public →
          </Link>
        </span>,
        { duration: 10000 },
      );
      setActive(null);
      // Clear the ?task= query if present so a page reload doesn't try
      // to open the now-approved task again.
      if (searchParams.get("task")) {
        searchParams.delete("task");
        setSearchParams(searchParams, { replace: true });
      }
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const toggleSelected = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selected.size === tasks.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(tasks.map((t) => t.id)));
    }
  };

  const openBatchDiff = () => {
    if (selected.size === 0) return;
    setBatchDiffOpen(true);
  };

  const batchApprove = async () => {
    if (selected.size === 0) return;
    setBatchBusy(true);
    let ok = 0;
    let fail = 0;
    for (const tid of selected) {
      try {
        await http.post(`/admin/pipeline/approvals/${tid}/approve`, { corrections: {} });
        ok++;
      } catch {
        fail++;
      }
    }
    setBatchBusy(false);
    setBatchDiffOpen(false);
    setSelected(new Set());
    load();
    if (fail === 0) {
      toast.success(`Batch approved ${ok} task(s).`);
    } else {
      toast.error(`Approved ${ok}, failed ${fail}. Reload and review remaining.`);
    }
  };

  // Summary counters used in the diff preview dialog.
  const statusCounts = (task) => {
    const cl = task?.draft?.checklist || [];
    const out = { FOUND: 0, PARTIAL: 0, NOT_FOUND: 0 };
    for (const c of cl) {
      const s = c?.status || "NOT_FOUND";
      if (out[s] !== undefined) out[s] += 1;
    }
    return out;
  };

  return (
    <AdminLayout>
      <div className="bp-container py-8">
        <h1 className="font-serif text-4xl mb-6">First-build approvals</h1>
        <p className="text-sm mb-6" style={{ color: "var(--bp-text-secondary)" }}>
          First publications are never automatic. Review the draft below alongside the evidence snapshots. Approve to publish as baseline v1 (locks brand identity + official domains). Corrections are stored as labeled data for future tuning.
        </p>

        {!active && (
          <>
            {selected.size > 0 && (
              <div
                className="mb-3 flex items-center gap-3 p-3 border"
                style={{ borderColor: "var(--bp-border)", background: "#F5F5F0" }}
                data-testid="batch-approve-bar"
              >
                <span className="text-sm font-mono">{selected.size} selected</span>
                <Button
                  onClick={openBatchDiff}
                  disabled={batchBusy}
                  className="rounded-none h-8 text-xs"
                  data-testid="batch-approve-btn"
                >
                  {batchBusy ? "Approving…" : `Review & approve ${selected.size} as-is →`}
                </Button>
                <button
                  onClick={() => setSelected(new Set())}
                  className="text-xs bp-link"
                >
                  clear
                </button>
              </div>
            )}
            <div className="bg-white border" style={{ borderColor: "var(--bp-border)" }}>
              <table className="w-full text-sm">
                <thead><tr className="border-b text-left" style={{ borderColor: "var(--bp-border)" }}>
                  <th className="px-4 py-3" style={{ width: 40 }}>
                    <input
                      type="checkbox"
                      checked={tasks.length > 0 && selected.size === tasks.length}
                      onChange={toggleSelectAll}
                      data-testid="approval-select-all"
                      aria-label="Select all approvals"
                    />
                  </th>
                  <th className="px-4 py-3 uppercase text-xs tracking-widest">Created</th>
                  <th className="px-4 py-3 uppercase text-xs tracking-widest">Brand</th>
                  <th className="px-4 py-3 uppercase text-xs tracking-widest">Evidences</th>
                  <th className="px-4 py-3 uppercase text-xs tracking-widest">Snapshots</th>
                  <th />
                </tr></thead>
                <tbody>
                  {tasks.map((t) => (
                    <tr key={t.id} className="border-b" style={{ borderColor: "var(--bp-border)" }} data-testid={`approval-row-${t.brand?.slug}`}>
                      <td className="px-4 py-3">
                        <input
                          type="checkbox"
                          checked={selected.has(t.id)}
                          onChange={() => toggleSelected(t.id)}
                          data-testid={`approval-select-${t.brand?.slug}`}
                          aria-label={`Select ${t.brand?.display_name}`}
                        />
                      </td>
                      <td className="px-4 py-3 font-mono text-xs">{(t.created_at || "").slice(0, 10)}</td>
                      <td className="px-4 py-3">{t.brand?.display_name} <span className="text-xs font-mono" style={{ color: "var(--bp-text-muted)" }}>· {t.brand?.sector_code}</span></td>
                      <td className="px-4 py-3 font-mono text-xs">{(t.evidences || []).length}</td>
                      <td className="px-4 py-3 font-mono text-xs">{(t.snapshots || []).length}</td>
                      <td className="px-4 py-3 text-right"><button onClick={() => open(t)} className="bp-link" data-testid={`open-approval-${t.brand?.slug}`}>Review →</button></td>
                    </tr>
                  ))}
                  {tasks.length === 0 && <tr><td colSpan={6} className="px-4 py-8 text-center" style={{ color: "var(--bp-text-muted)" }}>No approvals pending.</td></tr>}
                </tbody>
              </table>
            </div>
          </>
        )}

        {active && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Draft (editable) */}
            <div className="bg-white border p-5" style={{ borderColor: "var(--bp-border)" }}>
              <div className="uppercase text-xs tracking-[0.3em] mb-3" style={{ color: "var(--bp-text-muted)" }}>Draft — {active.brand?.display_name}</div>
              <div className="space-y-3">
                {CATS.map((cat) => {
                  const it = edits.checklist[cat] || { status: "NOT_FOUND", evidence_excerpt: "" };
                  return (
                    <div key={cat} className="border-b pb-2" style={{ borderColor: "var(--bp-border)" }} data-testid={`draft-row-${cat}`}>
                      <div className="flex items-center justify-between mb-1">
                        <div className="font-serif">{LABELS[cat]}</div>
                        <div className="flex gap-1">
                          {["FOUND", "PARTIAL", "NOT_FOUND"].map((s) => (
                            <button key={s} onClick={() => setEdits({ ...edits, checklist: { ...edits.checklist, [cat]: { ...it, status: s } } })} className={`px-2 py-0.5 text-xs uppercase border ${it.status === s ? "bg-black text-white border-black" : ""}`} style={{ borderColor: "var(--bp-border)" }} data-testid={`draft-status-${cat}-${s}`}>{s.replace("_", " ")}</button>
                          ))}
                        </div>
                      </div>
                      <Textarea rows={2} maxLength={300} value={it.evidence_excerpt} onChange={(e) => setEdits({ ...edits, checklist: { ...edits.checklist, [cat]: { ...it, evidence_excerpt: e.target.value } } })} data-testid={`draft-excerpt-${cat}`} />
                    </div>
                  );
                })}
                <div>
                  <label className="text-xs uppercase tracking-widest">Summary EN</label>
                  <Textarea rows={5} value={edits.summary_en} onChange={(e) => setEdits({ ...edits, summary_en: e.target.value })} data-testid="draft-summary-en" />
                </div>
                <div className="flex gap-2 pt-3">
                  <Button onClick={approve} className="rounded-none" data-testid="approval-approve"><Check size={14} className="mr-1" /> Approve &amp; publish as baseline v1</Button>
                  <Button variant="outline" className="rounded-none" onClick={() => setActive(null)}>Cancel</Button>
                </div>
              </div>
            </div>

            {/* Evidence + weak points */}
            <div>
              {active.weak_points && active.weak_points.length > 0 && (
                <div className="p-5 bg-white border mb-4" style={{ borderColor: "var(--bp-border)", background: "#FFF8E1" }} data-testid="weak-points">
                  <div className="uppercase text-xs tracking-widest mb-2" style={{ color: "#B97700" }}>Weak points (from Prompt E)</div>
                  <ul className="list-disc pl-5 text-sm">
                    {active.weak_points.map((w, i) => <li key={`wp-${i}-${w.slice(0, 20)}`}>{w}</li>)}
                  </ul>
                </div>
              )}
              <div className="bg-white border p-5" style={{ borderColor: "var(--bp-border)" }}>
                <div className="uppercase text-xs tracking-[0.3em] mb-3" style={{ color: "var(--bp-text-muted)" }}>Evidence snapshots ({active.snapshots?.length || 0})</div>
                <ul className="space-y-3 text-sm max-h-[300px] overflow-auto">
                  {(active.snapshots || []).map((s) => (
                    <li key={s.id} className="flex items-start gap-2 border-b pb-2" style={{ borderColor: "var(--bp-border)" }}>
                      <ExternalLink size={12} className="mt-1" />
                      <a href={s.url} target="_blank" rel="noopener noreferrer" className="bp-link break-all">{s.title || s.url}</a>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="bg-white border p-5 mt-4" style={{ borderColor: "var(--bp-border)" }}>
                <div className="uppercase text-xs tracking-[0.3em] mb-3" style={{ color: "var(--bp-text-muted)" }}>Evidence records ({active.evidences?.length || 0})</div>
                <ul className="space-y-2 text-xs max-h-[300px] overflow-auto">
                  {(active.evidences || []).map((e) => (
                    <li key={e.id} className="border-b pb-2" style={{ borderColor: "var(--bp-border)" }}>
                      <div className="flex justify-between font-mono text-[10px]" style={{ color: "var(--bp-text-muted)" }}>
                        <span>{e.category} · {e.specificity} · conf {e.extraction_confidence?.toFixed(2)}</span>
                        {e.register_verified && <span>reg: {e.register_verified}</span>}
                      </div>
                      <div className="mt-1">{e.evidence_excerpt}</div>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* Audit log — last 20 approvals with who/when/what. */}
        {!active && (
          <div className="mt-10" data-testid="approval-audit-log">
            <div className="flex flex-wrap items-baseline gap-3 mb-3">
              <h2 className="font-serif text-2xl">Recent approvals</h2>
              <input
                type="text"
                value={filterApprover}
                onChange={(e) => setFilterApprover(e.target.value)}
                placeholder="Filter by approver email…"
                className="border px-2 py-1 text-xs font-mono"
                style={{ borderColor: "var(--bp-border)", minWidth: 220 }}
                data-testid="audit-filter-approver"
              />
              <select
                value={filterSector}
                onChange={(e) => setFilterSector(e.target.value)}
                className="border px-2 py-1 text-xs font-mono bg-white"
                style={{ borderColor: "var(--bp-border)" }}
                data-testid="audit-filter-sector"
              >
                <option value="">All sectors</option>
                <option value="FASHION">FASHION</option>
                <option value="FURNITURE">FURNITURE</option>
                <option value="ELECTRONICS">ELECTRONICS</option>
              </select>
              {(filterApprover || filterSector) && (
                <button
                  onClick={() => { setFilterApprover(""); setFilterSector(""); }}
                  className="text-xs bp-link"
                  data-testid="audit-filter-clear"
                >
                  clear
                </button>
              )}
            </div>
            <div className="bg-white border" style={{ borderColor: "var(--bp-border)" }}>
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left" style={{ borderColor: "var(--bp-border)" }}>
                    <th className="px-4 py-3 uppercase text-xs tracking-widest">Approved</th>
                    <th className="px-4 py-3 uppercase text-xs tracking-widest">Brand</th>
                    <th className="px-4 py-3 uppercase text-xs tracking-widest">Approver</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {history.map((h) => (
                    <tr key={h.task_id} className="border-b" style={{ borderColor: "var(--bp-border)" }} data-testid={`audit-row-${h.brand?.slug}`}>
                      <td className="px-4 py-3 font-mono text-xs">{(h.approved_at || "").slice(0, 19).replace("T", " ")}</td>
                      <td className="px-4 py-3">
                        {h.brand?.display_name || <span style={{ color: "var(--bp-text-muted)" }}>—</span>}
                        <span className="text-xs font-mono ml-1" style={{ color: "var(--bp-text-muted)" }}>· {h.brand?.sector_code}</span>
                      </td>
                      <td className="px-4 py-3 text-xs">{h.approver_email || h.approver_name || <span style={{ color: "var(--bp-text-muted)" }}>—</span>}</td>
                      <td className="px-4 py-3 text-right">
                        {h.brand?.slug && (
                          <Link to={`/brands/${h.brand.slug}`} target="_blank" className="bp-link text-xs">
                            View public →
                          </Link>
                        )}
                      </td>
                    </tr>
                  ))}
                  {history.length === 0 && (
                    <tr><td colSpan={4} className="px-4 py-6 text-center" style={{ color: "var(--bp-text-muted)" }}>
                      No approvals recorded yet.
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Batch approve — diff preview dialog (item 1) */}
        <Dialog open={batchDiffOpen} onOpenChange={(v) => !v && !batchBusy && setBatchDiffOpen(false)}>
          <DialogContent className="max-w-2xl" data-testid="batch-approve-diff-dialog">
            <DialogHeader>
              <DialogTitle className="font-serif">
                Review before approving {selected.size} task(s)
              </DialogTitle>
            </DialogHeader>
            <div className="text-xs mb-3" style={{ color: "var(--bp-text-muted)" }}>
              You are about to publish these drafts verbatim (no corrections). Only proceed if you have
              already reviewed them offline. This action cannot be undone from this screen.
            </div>
            <div className="max-h-[420px] overflow-auto border" style={{ borderColor: "var(--bp-border)" }}>
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b text-left" style={{ borderColor: "var(--bp-border)" }}>
                    <th className="px-3 py-2 uppercase tracking-widest">Brand</th>
                    <th className="px-3 py-2 uppercase tracking-widest">Checklist</th>
                    <th className="px-3 py-2 uppercase tracking-widest">Summary (EN, preview)</th>
                  </tr>
                </thead>
                <tbody>
                  {tasks
                    .filter((t) => selected.has(t.id))
                    .map((t) => {
                      const c = statusCounts(t);
                      const preview = (t.draft?.summary?.en || "").slice(0, 140);
                      return (
                        <tr key={t.id} className="border-b" style={{ borderColor: "var(--bp-border)" }} data-testid={`batch-diff-row-${t.brand?.slug}`}>
                          <td className="px-3 py-2 align-top font-mono">
                            {t.brand?.slug}
                            <div className="text-[10px]" style={{ color: "var(--bp-text-muted)" }}>{t.brand?.sector_code}</div>
                          </td>
                          <td className="px-3 py-2 align-top font-mono">
                            <span style={{ color: "#166534" }}>{c.FOUND} FOUND</span>
                            {" · "}
                            <span style={{ color: "#B45309" }}>{c.PARTIAL} PARTIAL</span>
                            {" · "}
                            <span style={{ color: "var(--bp-text-muted)" }}>{c.NOT_FOUND} NOT_FOUND</span>
                          </td>
                          <td className="px-3 py-2 align-top" style={{ color: "var(--bp-text-secondary)" }}>
                            {preview ? preview + (preview.length >= 140 ? "…" : "") : <em style={{ color: "var(--bp-text-muted)" }}>no summary</em>}
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => setBatchDiffOpen(false)}
                disabled={batchBusy}
                className="rounded-none"
              >
                Cancel
              </Button>
              <Button
                onClick={batchApprove}
                disabled={batchBusy}
                className="rounded-none"
                data-testid="batch-approve-confirm-btn"
              >
                {batchBusy ? "Approving…" : `Approve ${selected.size} now →`}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
